<template>
  <UContainer class="py-12">
    <div class="flex items-center justify-between mb-8">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Releases</h1>
        <div class="flex gap-4 mt-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="type" type="radio" value="microtech" @change="fetchReleases" />
            <span
              :class="type === 'microtech' ? 'font-bold text-primary-600' : 'text-gray-500'"
              class="text-sm"
              >Microtech</span
            >
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="type" type="radio" value="connectelo" @change="fetchReleases" />
            <span
              :class="type === 'connectelo' ? 'font-bold text-primary-600' : 'text-gray-500'"
              class="text-sm"
              >ConnectELO</span
            >
          </label>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {{ releases.length }} Dateien verfügbar
        </p>
      </div>
      <div class="flex gap-2">
        <UButton color="gray" icon="i-heroicons-arrow-left" to="/" variant="ghost">
          Zurück
        </UButton>
        <UButton
          :loading="loading"
          color="gray"
          icon="i-heroicons-arrow-path"
          variant="ghost"
          @click="fetchReleases"
        >
          Neu laden
        </UButton>
      </div>
    </div>

    <UCard>
      <UTable :columns="columns" :data="releases" :loading="loading" class="w-full">
        <template #fileName-cell="{ row }">
          <div class="flex flex-col">
            <span class="font-semibold text-gray-900 dark:text-white">{{
              row.original.fileName
            }}</span>
            <span class="text-xs text-gray-500 font-mono">{{ row.original.objectName }}</span>
          </div>
        </template>

        <template #sizeBytes-cell="{ row }">
          <span class="text-gray-600 dark:text-gray-400">
            {{ formatSize(row.original.sizeBytes) }}
          </span>
        </template>

        <template #lastModified-cell="{ row }">
          <span class="text-gray-600 dark:text-gray-400">
            {{ formatDate(row.original.lastModified) }}
          </span>
        </template>

        <template #actions-cell="{ row }">
          <div class="flex justify-end gap-2 px-4">
            <UButton
              color="primary"
              size="xs"
              variant="subtle"
              @click="downloadRelease(row.original)"
            >
              Download
            </UButton>
            <UButton
              color="secondary"
              size="xs"
              variant="subtle"
              @click="copyReleaseLink(row.original)"
            >
              Link
            </UButton>
            <UButton
              v-if="type === 'microtech'"
              color="secondary"
              size="xs"
              variant="subtle"
              @click="openChangelog(row.original)"
            >
              Changelog
            </UButton>
          </div>
        </template>
      </UTable>

      <template v-if="!loading && releases.length === 0" #empty-state>
        <div class="flex flex-col items-center justify-center py-12 text-gray-500">
          <UIcon class="w-12 h-12 mb-4 opacity-20" name="i-heroicons-archive-box-x-mark" />
          <p>Keine Dateien gefunden.</p>
        </div>
      </template>
    </UCard>
  </UContainer>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'

interface Release {
  fileName: string
  objectName: string
  version: string
  sizeBytes: number
  lastModified: string
}

const route = useRoute()
const releases = ref<Release[]>([])
const loading = ref(false)
const type = ref<'microtech' | 'connectelo'>(
  (route.query.type as 'microtech' | 'connectelo') || 'microtech',
)

watch(
  () => route.query.type,
  (newType) => {
    if (newType === 'microtech' || newType === 'connectelo') {
      type.value = newType
      fetchReleases()
    }
  },
)

const columns = [
  { accessorKey: 'fileName', header: 'Datei' },
  { accessorKey: 'version', header: 'Version' },
  { accessorKey: 'sizeBytes', header: 'Größe', class: 'text-right' },
  { accessorKey: 'lastModified', header: 'Zuletzt geändert' },
  { id: 'actions', header: '' },
]

const fetchReleases = async () => {
  loading.value = true
  try {
    const response = await axios.get(`/api/registry/v1/releases/${type.value}`)
    releases.value = response.data
  } catch (error) {
    console.error('Failed to fetch releases', error)
  } finally {
    loading.value = false
  }
}

const formatSize = (bytes: number) => {
  if (!bytes) return '0 KB'
  return `${Math.round(bytes / 1024).toLocaleString()} KB`
}

const formatDate = (dateString: string) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const presign = async (objectName: string) => {
  const res = await axios.post(`/api/registry/v1/releases/${type.value}/presign`, { objectName })
  return res.data.url
}

const downloadRelease = async (row: Release) => {
  try {
    const url = await presign(row.objectName)
    window.location.href = url
  } catch (error) {
    console.error('Download failed', error)
  }
}

const copyReleaseLink = async (row: Release) => {
  try {
    const url = await presign(row.objectName)
    await navigator.clipboard.writeText(url)
    alert('Downloadlink in Zwischenablage')
  } catch (error) {
    console.error('Copy failed', error)
  }
}

const openChangelog = (row: Release) => {
  const version = row.version
  if (!version || version === '-') return
  const versionParts = version.split('.')
  const buildNumber = versionParts[versionParts.length - 1]
  window.open(`https://hilfe.microtech.de/15_programmaenderungen/#build-${buildNumber}`, '_blank')
}

onMounted(() => {
  fetchReleases()
})
</script>
